import { Controller } from "@hotwired/stimulus"

/**
 * Country Select Controller - handles country/state select integration
 * Works with Tom Select for enhanced dropdowns
 */
export default class extends Controller {
  static values = {
    setDefaultState: {
      default: true,
      type: Boolean
    }
  }
  static targets = ['country', 'stateSelect', 'stateInput']

  connect() {
    this.changed()
  }

  changed() {
    const countryCode = this.countryTarget.value
    if (!countryCode) return

    const stateSelect = this.stateSelectTarget
    const stateInput = this.stateInputTarget
    let stateValue = this.resetStateValues(stateInput, stateSelect)

    fetch('/countries/' + countryCode)
      .then(response => response.json())
      .then(data => {
        this.updateState(data.states || [], stateValue)
      })
  }

  resetStateValues(stateInput, stateSelect) {
    let stateValue

    if (stateInput.tagName === "SELECT" && stateInput.multiple) {
      stateValue = Array.from(stateInput.selectedOptions).map(option => option.value)
    } else {
      stateValue = stateInput.value
    }

    // Clear Tom Select instance if exists, otherwise clear regular select
    if (stateSelect.tomselect) {
      stateSelect.tomselect.clear()
      stateSelect.tomselect.clearOptions()
    } else {
      stateSelect.value = ''
      while (stateSelect.firstChild) {
        stateSelect.removeChild(stateSelect.firstChild)
      }
    }

    if (!stateInput.tomselect) {
      stateInput.value = ''
    }

    return stateValue
  }

  updateState(list = [], stateValue = '') {
    const stateSelect = this.stateSelectTarget
    const stateInput = this.stateInputTarget

    if (list.length) {
      this.setFixedStateValues(list, stateSelect, stateValue)
    } else {
      this.setFreeformStateValues(stateInput, stateValue)
    }

    this.toggleElement(stateInput, list.length)
    this.toggleElement(stateSelect, !list.length)
  }

  setFixedStateValues(list, stateSelect, stateValue) {
    // Check if Tom Select is initialized
    if (stateSelect.tomselect) {
      // Add options to Tom Select
      list.forEach(state => {
        stateSelect.tomselect.addOption({ value: state.code, text: state.name })
      })
      stateSelect.tomselect.refreshOptions(false)

      // Set the value
      const valueToSet = this.getStateValueToSet(list, stateValue)
      if (valueToSet) {
        stateSelect.tomselect.setValue(valueToSet)
      }
    } else {
      // Regular select - add options directly
      list.forEach(state => {
        const option = new Option(state.name, state.code, false, false)
        stateSelect.appendChild(option)
      })

      const valueToSet = this.getStateValueToSet(list, stateValue)
      if (valueToSet) {
        stateSelect.value = valueToSet
      }
      stateSelect.dispatchEvent(new Event('change'))
    }
  }

  getStateValueToSet(list, stateValue) {
    if (Array.isArray(stateValue)) {
      const foundValues = stateValue
        .map(value => list.find(item => item.code === value))
        .filter(Boolean)
      return foundValues.length ? foundValues.map(item => item.code) : this.defaultStateValue(list)
    } else {
      return stateValue && list.find(item => item.code === stateValue)
        ? stateValue
        : this.defaultStateValue(list)
    }
  }

  defaultStateValue(list) {
    return this.setDefaultStateValue ? list[0]?.code : null
  }

  setFreeformStateValues(stateInput, stateValue) {
    if (stateInput.tagName === "SELECT" && stateInput.multiple) {
      if (Array.isArray(stateValue)) {
        stateValue.forEach(value => {
          const option = new Option(value, value, true, true)
          stateInput.appendChild(option)
        })
      }
    } else {
      stateInput.value = stateValue || ''
    }
    stateInput.dispatchEvent(new Event('change'))
  }

  toggleElement(element, isHide) {
    element.disabled = isHide
    const parentElement = element.closest('.state-wrap')
    if (parentElement) {
      parentElement.style.display = isHide ? 'none' : 'block'
    }
    this.toggleRequiredState(element, isHide)

    // Also disable/enable Tom Select wrapper if present
    if (element.tomselect) {
      if (isHide) {
        element.tomselect.disable()
      } else {
        element.tomselect.enable()
      }
    }
  }

  toggleRequiredState(element, isHide) {
    if (isHide) {
      this.cacheRequiredState(element)
      element.removeAttribute('required')
    } else {
      if (element.dataset.required) {
        element.setAttribute('required', 'required')
      }
    }
  }

  cacheRequiredState(element) {
    if (element.required) {
      element.dataset.required = 'required'
    }
  }
}
